import { useMemo } from 'react';
import PropTypes from 'prop-types';

import { ROUTES } from '#/config/routes';
import { AppDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import TruncatedTextWithTooltip from '#/modules/commonComponents/table/TruncatedTextWithTooltip';
import { TableProvider } from '#/modules/commonComponents/table/TableContext';
import Table from '#/modules/commonComponents/table/Table';
import useTable from '#/hooks/table/useTable';

function formatMtrLabel(mtr) {
  if (!mtr) {
    return '—';
  }
  const no = mtr.mtrNo || '';
  const name = mtr.name || '';
  if (no && name) {
    return `${no} ${name}`;
  }
  return no || name || '—';
}

function mapWorkOrderRows(workOrderNos = [], workOrderNoToId = new Map()) {
  return (workOrderNos ?? [])
    .filter(Boolean)
    .map((workOrderNo, index) => {
      const no = String(workOrderNo);
      const workOrderId = workOrderNoToId.get(no) ?? null;
      return {
        id: workOrderId != null ? String(workOrderId) : `wo-${no}-${index}`,
        workOrderNo: no,
        workOrderId,
      };
    });
}

function buildColumns() {
  return [
    {
      id: 'workOrderNo',
      header: '工單號',
      accessorKey: 'workOrderNo',
      size: 220,
      enableColumnFilter: true,
      meta: {
        cellClassName: 'td_left',
        noTruncate: true,
        // client-side filter UI（不走 server list）
        filterVariant: 'text',
        filterMode: 'client',
        filterPlaceholder: '搜尋工單號',
      },
      cell: ({ row }) => {
        const { workOrderNo, workOrderId } = row.original;
        const canOpen = Boolean(workOrderId);
        return (
          <TruncatedTextWithTooltip
            text={workOrderNo}
            detailPath={canOpen ? ROUTES.workOrderDetail(workOrderId) : null}
            tooltipContent={canOpen ? '雙擊開啟工單（新分頁）' : undefined}
          />
        );
      },
    },
  ];
}

const AffectedWorkOrdersDialog = ({
  open,
  onOpenChange,
  mtr,
  workOrderNos = [],
  workOrderNoToId = new Map(),
}) => {
  const rows = useMemo(
    () => mapWorkOrderRows(workOrderNos, workOrderNoToId),
    [workOrderNos, workOrderNoToId],
  );

  const columns = useMemo(() => buildColumns(), []);
  const tableState = useTable(
    rows,
    columns,
    { current_page: 1, page_size: 10, total_count: 0, total_page: 1 },
    {
      serverSide: false,
      disableGlobalFilter: true,
      enableRowSelection: false,
      initialPageSize: 200,
    },
  );

  const description = (
    <span className="truncate text-base font-medium">
      {formatMtrLabel(mtr)}
    </span>
  );

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="影響工單"
      description={description}
      headerVariant="primary"
      maxWidthClassName={dialogWideMaxWidthClassName}
      contentClassName={dialogContentCompactGapClassName}
      onCancel={() => onOpenChange(false)}
      cancelText="關閉"
      showCancel
    >
      {rows.length === 0 ? (
        <p className="py-6 text-center text-base text-muted-foreground">
          尚無影響工單
        </p>
      ) : (
        <TableProvider value={tableState}>
          <Table
            loading={false}
            containerClassName="max-h-96 max-w-full overflow-auto"
          />
        </TableProvider>
      )}
    </AppDialog>
  );
};

AffectedWorkOrdersDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  mtr: PropTypes.shape({
    id: PropTypes.string,
    mtrNo: PropTypes.string,
    name: PropTypes.string,
  }),
  workOrderNos: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  workOrderNoToId: PropTypes.instanceOf(Map),
};

export default AffectedWorkOrdersDialog;

